<script setup>
import { useDialogPluginComponent } from 'quasar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import HrBasicDialog from '../HrBasicDialog.vue';
import HrBtn from '../HrBtn.vue';

/**
 * Destructive confirmation dialog presented via confirmDestructiveDialog() with a red confirm button.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrConfirmDestructiveDialog' });

const props = defineProps({
  /** Dialog headline. */
  title: {
    type: String,
    required: true,
  },
  /** Supporting text describing the irreversible consequence of the action. */
  message: {
    type: String,
    required: true,
  },
  /**
   * Label for the destructive confirm action button.
   * Required — M3 specifies action-specific labels for destructive actions
   * (e.g. "Delete", "Remove", "Discard") rather than a generic "Confirm".
   */
  confirmLabel: {
    type: String,
    required: true,
  },
  /** Label for the cancel action button. Defaults to the common.buttonCancel translation. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   * Defaults to true — a destructive confirm dialog requires an explicit choice.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { t } = useI18n();
const effectiveCancelLabel = computed(() => props.cancelLabel ?? t('common.buttonCancel'));
</script>

<template>
  <hr-basic-dialog
    ref="dialogRef"
    data-e2e="dialog-confirm-destructive"
    :persistent="persistent"
    @hide="onDialogHide"
  >
    <div class="hr-dialog">
      <p class="hr-dialog__headline hr-headline-small">
        {{ title }}
      </p>
      <p class="hr-dialog__supporting-text hr-body-medium">
        {{ message }}
      </p>
      <div class="hr-dialog__actions">
        <!-- @todo When the legacy theme is removed: remove :legacy-props -->
        <hr-btn
          variant="text"
          data-e2e="button-cancel"
          :label="effectiveCancelLabel"
          :legacy-props="{ flat: true, color: 'secondary' }"
          @click="() => onDialogCancel()"
        />
        <hr-btn
          variant="text"
          destructive
          data-e2e="button-confirm"
          :label="confirmLabel"
          :legacy-props="{ flat: true, color: 'negative' }"
          @click="() => onDialogOK()"
        />
      </div>
    </div>
  </hr-basic-dialog>
</template>

<style lang="scss" scoped>
.hr-dialog {
  background: var(--app-surface-container-high);
  border-radius: var(--app-shape-corner-extra-large);
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.hr-dialog__headline {
  color: var(--app-on-surface);
  margin: 0 0 16px;
}

.hr-dialog__supporting-text {
  color: var(--app-on-surface-variant);
  margin: 0;
  /* Supporting text is plain text; honor authored line breaks (\n) without allowing HTML. */
  white-space: pre-line;
}

.hr-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
</style>
